import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import { SetFiltering } from 'state/actions';
import { ListingTable } from "components/listing/ListingTable";
import { FleetService } from "services/FleetService";
import { FormTextInput } from "components/form-fields";
import { Button, LinkButton } from "components/buttons";
import { Tooltip } from "components/tooltip";
import { AlertConfirm, NotificationToaster } from "components/notifications";
import { UserAccess } from 'components/user-access';
import { PageRestricted } from 'pages/errors/page-restricted';
import { Intent } from "@blueprintjs/core";
import { Filter, FilterButton } from 'components/filtering';

export function EventTypeListing(props) {

    const requiredAction = "FMS:Manage";
    const filterValueName = "eventTypeFilters";

    const tableHeaders = [
        "Event Type",
        "Schedule Type",
        "Schedule",
        "Costs",
        "Actions"
    ];

    const sortableHeaders = [
        { headerName: "Event Type", sortName: "name" }
    ];

    const dispatch = useDispatch();
    var navigate = useNavigate();

    const requestCount = 25;
    const debounceMilliseconds = 500;

    // Table state
    const [tableRows, setTableRows] = useState([]);
    const [isLoadingData, setIsLoadingData] = useState(true);
    const [totalRecords, setTotalRecords] = useState(0);

    // Search/filter state
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
    const [showFilters, setShowFilters] = useState(false);
    const [pageFilters, setPageFilters] = useState([]);
    const [appliedFilters, setAppliedFilters] = useState(null);

    //Paging/sorting state
    const [pageNumber, setPageNumber] = useState(1);
    const [tableSortBy, setTableSortBy] = useState({ headerName: "Event Type", sortName: "name" });
    const [tableSortDir, setTableSortDir] = useState("A");

    // Modal/confirmation state
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [selectedEventTypeId, setSelectedEventTypeId] = useState(null);
    const [showHideConfirm, setShowHideConfirm] = useState(false);
    const [selectedEventTypeToHide, setSelectedEventTypeToHide] = useState(null);

    const filterOptions = useSelector(state => state.filters[filterValueName] || []);
    const includeHiddenFilter = filterOptions.find(f => f.name === "INCLUDEHIDDEN");
    const inputRef = useRef(null);
    
    useEffect(() => {
        if (appliedFilters) {
            searchEventTypes();
        }
    }, [debouncedSearchTerm, pageNumber, tableSortBy, tableSortDir, appliedFilters]);

    useEffect(() => {
        const handler = setTimeout(() => setDebouncedSearchTerm(searchTerm), debounceMilliseconds);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    useEffect(() => {
        if (pageFilters.length < 1) {
            let pageFilterList = [
                {
                    name: "INCLUDEHIDDEN",
                    displayName: "Include Hidden",
                    singleSelect: true,
                    items: [
                        { id: "true", name: "Yes" },
                        { id: "false", name: "No" }
                    ]
                }
            ];
            const defaultFilters = [{ name: 'INCLUDEHIDDEN', displayName: 'Include Hidden', value: "False" }];
            if (filterOptions.length === 0) {
                dispatch(SetFiltering(filterValueName, defaultFilters));
            }
            setPageFilters(pageFilterList);
        }
    }, []);

    function onTableSort(header, direction) {
        setTableSortBy(header);
        setTableSortDir(direction);
    }

    function searchEventTypes() {
        setIsLoadingData(true);
        let includeHidden = includeHiddenFilter?.value === "true";
        const filters = [{
            key: "includehidden",
            value: `${includeHidden}`
        }];
        FleetService.searchEventTypes(
            requestCount,
            pageNumber,
            debouncedSearchTerm,
            tableSortBy.sortName,
            tableSortDir,        
            filters
        )
            .then((response) => {
                let data = response.data;
                if (response && response.data) {
                    setTableRows(
                        data.map(eventType => 
                            renderTableRow(
                                eventType
                            )
                        )
                    );
                    setTotalRecords(response.totalCount || data.length);
                } else {
                    setTableRows([]);
                    setTotalRecords(0);
                }
            })
            .catch(() => {
                setTableRows([]);
                setTotalRecords(0);
            })
            .finally(() => {
                setIsLoadingData(false);
            });
    }

    function onSearchChange(e) {
        setSearchTerm(e.target.value);
    }

    function onFilterChange(listingFilters) {
        setPageNumber(1);
        setAppliedFilters(listingFilters ?? []);
    }
    function onToggleFilter() {
        setShowFilters((prevState) => {
            return !prevState;
        })
    }

    function renderTableRow(eventType) {
        const deleteTooltipContentInner = eventType.eventsExist
            ? "Cannot delete: events exist for this type."
            : "Delete";

        const deleteTooltipContent = eventType.hasSpecialProcessing
            ? "Cannot delete: special processing is enabled."
            : deleteTooltipContentInner;

        return ([
            <Link key={`link-${eventType.id}`} to={`/fleet-settings/event-types/${eventType.id}/details`}>{eventType.name}</Link>,
            eventType.scheduleType,
            eventType.schedule,
            eventType.costs,
            <div key={`${eventType.id}-event-type-actions`} className="button-row-small inline-items">
                <Tooltip
                    content={
                        !eventType.cloneable
                            ? "Cannot clone this type of event."
                            : "Clone"
                    }
                >
                    <Button
                        icon="duplicate"
                        minimal={true}
                        large={false}
                        intent="primary"
                        disabled={!eventType.cloneable}
                        onClick={() => navigate(`/fleet-settings/event-types/create?cloneId=${eventType.id}`)}
                    />
                </Tooltip>
                <Tooltip content={eventType.hidden ? "Show Event Type" : "Hide Event Type"}>
                    <Button
                        icon={eventType.hidden ? "eye-off" : "eye-open"}
                        minimal={true}
                        large={false}
                        intent={eventType.hidden ? "none" : "primary"}
                        onClick={() => {
                            if (eventType.hidden) {
                                setIsLoadingData(true);
                                FleetService.hideEventType(eventType.id, false)
                                    .then(() => {
                                        NotificationToaster.show(Intent.SUCCESS, "Event type shown successfully.");
                                        searchEventTypes();
                                    })
                                    .catch(() => {
                                        NotificationToaster.show(Intent.DANGER, "Failed to show event type.");
                                        setIsLoadingData(false);
                                    });
                            } else {
                                setSelectedEventTypeToHide(eventType);
                                setShowHideConfirm(true);
                            }
                        }}
                    />
                </Tooltip>
                <Tooltip content={deleteTooltipContent}>
                    <span>
                        <Button
                            icon="trash"
                            minimal={true}
                            large={false}
                            className="bp6-intent-danger"
                            disabled={eventType.lckEventTypeId || eventType.hasSpecialProcessing || eventType.eventsExist}
                            onClick={() => {
                                if (!(eventType.hasSpecialProcessing || eventType.eventsExist)) {
                                    setSelectedEventTypeId(eventType.id);
                                    setShowDeleteConfirm(true);
                                }
                            }}
                        />
                    </span>
                </Tooltip>
            </div>
        ]);
    }

    return (
        <UserAccess
            perform={requiredAction}
            yes={() => (
                <div className="row">
                    <h2>Event Types</h2>
                    <div className="spacer-bottom">
                        <LinkButton
                            text="Fleet Overview"
                            intent="primary"
                            className="spacer-right"
                            href="/fleet/overview"
                            loading={isLoadingData}
                        />
                        <LinkButton
                            text="Fleet Planner"
                            intent="primary"
                            className="spacer-right"
                            href="/fleet/manage"
                            loading={isLoadingData}
                        />
                        <LinkButton
                            text="Add Event Type"
                            intent="primary"
                            href="/fleet-settings/event-types/create"
                            loading={isLoadingData}
                        />
                    </div>
                    <div className="filter-controls inline-items spacer-bottom">
                        <div className="inline-item fill">
                            <div className="form-field-full">
                                <FormTextInput
                                    inputRef={inputRef}
                                    placeholder="Search event type name"
                                    onChange={onSearchChange}
                                    value={searchTerm}
                                    large
                                    icon="search"
                                    id="event-type-search-field"
                                    loading={isLoadingData}
                                />
                            </div>
                        </div>
                        <Filter
                            filterName={filterValueName}
                            visible={showFilters}
                            filters={pageFilters}
                            onUpdate={onFilterChange}
                            loading={isLoadingData}
                            onClickOutside={onToggleFilter}
                            numOfColumns={3}
                        /> 
                        <FilterButton onToggle={onToggleFilter} filters={appliedFilters} />
                    </div>
                    <ListingTable
                        id="listing-table-fleet-event-type"
                        headers={tableHeaders}
                        data={tableRows}
                        totalRecordCount={totalRecords}
                        loadingData={isLoadingData}
                        pageable={true}
                        pageSize={requestCount}
                        pageNumber={pageNumber}
                        onPagingChange={(newPageNumber) => setPageNumber(newPageNumber)}
                        sortable
                        sortableHeaders={sortableHeaders}
                        sortedBy={tableSortBy}
                        sortedDir={tableSortDir}
                        onSort={onTableSort}
                    />
                    <AlertConfirm
                        title="Delete Event Type"
                        isOpen={showDeleteConfirm}
                        onConfirm={() => {
                            setShowDeleteConfirm(false);
                            setSelectedEventTypeId(null);
                            setIsLoadingData(true);
                            setTableRows([]);
                            if (selectedEventTypeId) {
                                FleetService.deleteEventType(selectedEventTypeId)
                                    .then(() => {
                                        NotificationToaster.show(Intent.SUCCESS, "Event type deleted successfully.");
                                        searchEventTypes();
                                    })
                                    .catch(() => {
                                        NotificationToaster.show(Intent.DANGER, "Failed to delete event type.");
                                        setIsLoadingData(false);
                                    });
                            } else {
                                setIsLoadingData(false);
                            }
                        }}
                        onCancel={() => {
                            setShowDeleteConfirm(false);
                            setSelectedEventTypeId(null);
                        }}
                        confirmButtonIntent="danger"
                        confirmButtonText="Delete"
                    >
                        <p>Are you sure you want to delete this event type?</p>
                    </AlertConfirm>
                    <AlertConfirm
                        title="Hide Event Type"
                        isOpen={showHideConfirm}
                        onConfirm={() => {
                            setShowHideConfirm(false);
                            setIsLoadingData(true);
                            FleetService.hideEventType(selectedEventTypeToHide.id, true)
                                .then(() => {
                                    NotificationToaster.show(Intent.SUCCESS, "Event type hidden successfully.");
                                    setSelectedEventTypeToHide(null);
                                    searchEventTypes();
                                })
                                .catch(() => {
                                    NotificationToaster.show(Intent.DANGER, "Failed to hide event type.");
                                    setIsLoadingData(false);
                                    setSelectedEventTypeToHide(null);
                                });
                        }}
                        onCancel={() => {
                            setShowHideConfirm(false);
                            setSelectedEventTypeToHide(null);
                        }}
                        confirmButtonIntent="danger"
                        confirmButtonText="Hide"
                    >
                        <p>Are you sure you want to hide this event type?</p>
                        {selectedEventTypeToHide && selectedEventTypeToHide.eventsExist && (
                            <p>Events of this type not in progress will be removed from the system.</p>
                        )}
                    </AlertConfirm>
                </div>
            )}
            no={() => (
                <div className="row">
                    <PageRestricted />
                </div>
            )}
        />
    );
}